import React, { Component } from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import PropTypes from "prop-types";
import { Header, Table } from "semantic-ui-react";
import CreditCostText from "../../../spa/component/ui/CreditCostText";
import ProductId from "~/constants/ProductId";
import TourJobType from "~/constants/TourJob/TourJobType";

@observer
class PriceTableForPricingMethodBasePriceAndPerPanorama extends Component {
  @computed
  get isDetailedTable() {
    const { panoramasCount, tourJobTypeId } = this.props;

    return (
      panoramasCount > 0 && this.panoramaCreditCost > 0 && tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
    );
  }

  @computed
  get panoramaCreditCost() {
    const { tourJobTypeId, productDiscountedCreditCosts } = this.props;
    return tourJobTypeId === TourJobType.IMMERSIVE_3D_TOUR_PANORAMAS
      ? productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_PANORAMAS]
      : productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN];
  }

  /**
   * @returns {number}
   */
  @computed
  get totalCreditCost() {
    const { panoramasCount, newPanoramasCount, panoramaProduct, productDiscountedCreditCosts } = this.props;

    const totalPanoramaCreditCost =
      this.calculatePanoramaCreditCost(panoramaProduct, panoramasCount + newPanoramasCount, this.panoramaCreditCost) -
      this.calculatePanoramaCreditCost(panoramaProduct, panoramasCount, this.panoramaCreditCost);

    if (panoramasCount > 0) {
      return totalPanoramaCreditCost;
    }

    return (
      productDiscountedCreditCosts[ProductId.PUBLISH_TOUR] +
      productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_BASE] +
      totalPanoramaCreditCost
    );
  }

  /**
   * @returns {number}
   */
  calculatePanoramaCreditCost(panoramaProductId, panoramasCount, panoramaCreditCost) {
    if (panoramaProductId === 0) {
      return 0;
    }

    let creditCostPerPanorama = 1;

    if (panoramaProductId === ProductId.IMMERSIVE_3D_TOUR_PANORAMAS) {
      // Because we want to charge 0.5 credits per panorama,
      // and we cannot create a product with a credit cost that has decimals
      // We have created a service product, and we will determine the credits for the service product
      creditCostPerPanorama = 0.5;
    }

    return Math.ceil(panoramasCount * creditCostPerPanorama) * panoramaCreditCost;
  }

  render() {
    const { panoramasCount, newPanoramasCount, panoramaProduct, productDiscountedCreditCosts } = this.props;

    const publishTourCreditCost = productDiscountedCreditCosts[ProductId.PUBLISH_TOUR];
    const immersive3dTourBaseCreditCost = productDiscountedCreditCosts[ProductId.IMMERSIVE_3D_TOUR_BASE];

    return (
      <>
        {this.isDetailedTable && <Header content="Summary" />}
        <Table striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>{!this.isDetailedTable && "Summary"}</Table.HeaderCell>
              {this.isDetailedTable && (
                <>
                  <Table.HeaderCell>Total</Table.HeaderCell>
                  <Table.HeaderCell>Existing</Table.HeaderCell>
                </>
              )}
              <Table.HeaderCell>New</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>Base price</Table.Cell>
              {this.isDetailedTable && (
                <>
                  <Table.Cell>
                    <CreditCostText cost={publishTourCreditCost + immersive3dTourBaseCreditCost} />
                  </Table.Cell>
                  <Table.Cell>
                    - <CreditCostText cost={publishTourCreditCost + immersive3dTourBaseCreditCost} />
                  </Table.Cell>
                </>
              )}
              <Table.Cell>
                {panoramasCount === 0 ? (
                  <CreditCostText cost={publishTourCreditCost + immersive3dTourBaseCreditCost} />
                ) : (
                  "PAID"
                )}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Panoramas</Table.Cell>
              {this.isDetailedTable && (
                <>
                  <Table.Cell>{panoramasCount + newPanoramasCount}</Table.Cell>
                  <Table.Cell>- {panoramasCount}</Table.Cell>
                </>
              )}
              <Table.Cell>{newPanoramasCount}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Credit Cost</Table.Cell>
              {this.isDetailedTable && (
                <>
                  <Table.Cell>
                    <CreditCostText
                      cost={this.calculatePanoramaCreditCost(
                        panoramaProduct,
                        panoramasCount + newPanoramasCount,
                        this.panoramaCreditCost,
                      )}
                    />
                  </Table.Cell>
                  <Table.Cell>
                    -{" "}
                    <CreditCostText
                      cost={this.calculatePanoramaCreditCost(panoramaProduct, panoramasCount, this.panoramaCreditCost)}
                    />
                  </Table.Cell>
                </>
              )}
              <Table.Cell>
                <CreditCostText
                  cost={
                    this.calculatePanoramaCreditCost(
                      panoramaProduct,
                      panoramasCount + newPanoramasCount,
                      this.panoramaCreditCost,
                    ) - this.calculatePanoramaCreditCost(panoramaProduct, panoramasCount, this.panoramaCreditCost)
                  }
                />
              </Table.Cell>
            </Table.Row>
          </Table.Body>
          <Table.Footer>
            <Table.Row>
              <Table.HeaderCell>
                <strong>Total</strong>
              </Table.HeaderCell>
              {this.isDetailedTable && (
                <>
                  <Table.HeaderCell />
                  <Table.HeaderCell />
                </>
              )}
              <Table.HeaderCell>
                <strong>
                  <CreditCostText cost={this.totalCreditCost} />
                </strong>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Footer>
        </Table>
        <p>The price for an Immersive 3D Tour is:</p>
        <Table celled textAlign="center">
          <Table.Body>
            <Table.Row>
              <Table.Cell colSpan="2">
                <strong>Base price</strong>
                <br />
                <CreditCostText cost={publishTourCreditCost + immersive3dTourBaseCreditCost} />
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>
                <strong>With 2D floor plan</strong>
                <br />1 credit per panorama
              </Table.Cell>
              <Table.Cell>
                <strong>Without 2D floor plan</strong>
                <br />
                0.5 credit per panorama
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </>
    );
  }
}

PriceTableForPricingMethodBasePriceAndPerPanorama.propTypes = {
  tourJobTypeId: PropTypes.number.isRequired,
  panoramasCount: PropTypes.number.isRequired,
  newPanoramasCount: PropTypes.number.isRequired,
  panoramaProduct: PropTypes.number.isRequired,
  productDiscountedCreditCosts: PropTypes.shape({
    [ProductId.PUBLISH_TOUR]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_PANORAMAS_WITH_FLOOR_PLAN]: PropTypes.number.isRequired,
    [ProductId.IMMERSIVE_3D_TOUR_BASE]: PropTypes.number.isRequired,
  }).isRequired,
};

export default PriceTableForPricingMethodBasePriceAndPerPanorama;
